import { ArrowDownWideNarrow, RefreshCw, Search, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ALL_FILTER_VALUE } from '@/lib/filter-constants';

import type { ComparisonSortBy } from './ComparisonSection';

interface ComparisonHeaderProps {
  hasComparison: boolean;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: ComparisonSortBy;
  onSortChange: (sortBy: ComparisonSortBy) => void;
  /** Selected Type filter value (ALL_FILTER_VALUE when unfiltered). */
  typeFilter: string;
  /** Distinct product types available to filter on. */
  typeOptions: string[];
  onTypeFilterChange: (value: string) => void;
  /** Selected Surrender-period filter value (ALL_FILTER_VALUE when unfiltered). */
  surrenderFilter: string;
  /** Distinct surrender-period years available to filter on. */
  surrenderOptions: number[];
  onSurrenderFilterChange: (value: string) => void;
}

export function ComparisonHeader({
  hasComparison,
  onRegenerate,
  isRegenerating = false,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  typeFilter,
  typeOptions,
  onTypeFilterChange,
  surrenderFilter,
  surrenderOptions,
  onSurrenderFilterChange,
}: ComparisonHeaderProps) {
  const [showSearch, setShowSearch] = useState(false);

  if (!hasComparison) return null;

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Column filters: Type + Surrender */}
        <Select
          value={typeFilter}
          onValueChange={onTypeFilterChange}
          disabled={typeOptions.length === 0}
        >
          <SelectTrigger className="h-8 w-44 text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER_VALUE}>All Types</SelectItem>
            {typeOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={surrenderFilter}
          onValueChange={onSurrenderFilterChange}
          disabled={surrenderOptions.length === 0}
        >
          <SelectTrigger className="h-8 w-40 text-xs">
            <SelectValue placeholder="Surrender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER_VALUE}>All Surrender</SelectItem>
            {surrenderOptions.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option} yr
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort tabs */}
        <div className="flex items-center rounded-md border border-border text-xs">
          <button
            onClick={() => onSortChange('match')}
            className={`px-2.5 py-1.5 rounded-l-md transition-colors ${
              sortBy === 'match' ? 'text-white font-medium' : 'hover:bg-muted'
            }`}
            style={sortBy === 'match' ? { backgroundColor: 'hsl(211 47% 20%)' } : undefined}
          >
            Match Score
          </button>
          <button
            onClick={() => onSortChange('sp500')}
            className={`px-2.5 py-1.5 rounded-r-md border-l border-border transition-colors ${
              sortBy === 'sp500' ? 'text-white font-medium' : 'hover:bg-muted'
            }`}
            style={sortBy === 'sp500' ? { backgroundColor: 'hsl(211 47% 20%)' } : undefined}
          >
            <ArrowDownWideNarrow className="mr-1 inline h-3 w-3" />
            S&P Cap
          </button>
        </div>

        {/* Unified search */}
        {showSearch || searchQuery ? (
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search carriers or products..."
              className="h-8 w-72 border-border bg-background pl-8 pr-8 text-sm text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-primary"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => {
                  onSearchChange('');
                  setShowSearch(false);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setShowSearch(true)}>
            <Search className="mr-1.5 h-3.5 w-3.5" />
            Search
          </Button>
        )}

        {/* Regenerate */}
        {onRegenerate && (
          <Button variant="ghost" size="sm" onClick={onRegenerate} disabled={isRegenerating}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
            {isRegenerating ? 'Regenerating...' : 'Regenerate'}
          </Button>
        )}
      </div>
    </div>
  );
}
